interface ToastColors {
  background: string;
  border: string;
  textStrong: string;
  textMuted: string;
  hoverStrong: string;
}

// Function to parse the variant and return corresponding colors
function parseVariant(variant: string | null): ToastColors {
  switch (variant) {
    case 'destructive':
      return {
        background: 'bg-destructive-200',
        border: 'border-destructive-300',
        textStrong: 'text-destructive-950',
        textMuted: 'text-destructive-900',
        hoverStrong: 'hover:text-destructive-950',
      };
    default:
      return {
        background: 'bg-white',
        border: 'border-border',
        textStrong: 'text-foreground',
        textMuted: 'text-muted-foreground',
        hoverStrong: 'hover:text-foreground',
      };
  }
}

// Initialize toasts by adding event listeners to elements with [data-toast]
export function initToasts() {
  const elements = document.querySelectorAll('[data-toast]');
  elements.forEach((element) => {
    const timeoutString = element.getAttribute('data-toast-timeout') ?? '';
    const timeout = isNaN(parseInt(timeoutString, 10))
      ? undefined
      : parseInt(timeoutString, 10);
    const title =
      element.getAttribute('data-toast-title') || 'Default toast title';
    const message =
      element.getAttribute('data-toast-message') || 'Default toast message';
    const variant = element.getAttribute('data-toast-variant') || 'default';
    element.addEventListener('click', () =>
      createToast(title, message, timeout, variant),
    );
  });
}

// Function to create a toast
export function createToast(
  title: string,
  message: string,
  timeout: number = 0,
  variant: string = 'default',
) {
  const toast = document.createElement('div');
  const colors = parseVariant(variant);

  toast.classList.add(
    'toast-item',
    'fixed',
    'left-0',
    '-translate-x-1/2',
    'flex',
    'items-center',
    'p-4',
    'rounded-lg',
    'border',
    'shadow-xl',
    'w-11/12',
    'max-w-md',
    'md:left-1/2',
    colors.background,
    colors.border,
    colors.textStrong,
  );

  toast.innerHTML = `
    <button data-toast-close class="absolute top-2 right-2 text-sm w-4 h-4 ${colors.textMuted} ${colors.hoverStrong}">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="size-4">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"></path>
      </svg>
    </button>
    <div class="flex flex-row items-center gap-4">
      <div>
        <p class="text-sm text-left font-semibold">${title}</p>
        <p class="text-sm text-left ${colors.textMuted}">${message}</p>
      </div>
    </div>
  `;

  const toastContainer = document.getElementById('toast-container');
  if (toastContainer) {
    toastContainer.prepend(toast);
    updateToastPositions();
    handleClose(toast, timeout);
  }
}

function updateToastPositions() {
  const toasts = document.querySelectorAll<HTMLElement>('.toast-item');
  toasts.forEach((toast, index) => {
    const finalBottom = index * (toast.offsetHeight + 16) + 16;
    toast.style.bottom = `-${toast.offsetHeight}px`;
    void toast.offsetWidth;
    toast.style.bottom = `${finalBottom}px`;
  });
}

function handleClose(toast: HTMLElement, timeoutDuration: number) {
  const close = toast.querySelector('[data-toast-close]');
  let timerId: number;

  close?.addEventListener('click', () => {
    hideToast(toast);
    clearTimeout(timerId);
  });

  if (timeoutDuration === 0) {
    return;
  }

  timerId = window.setTimeout(() => hideToast(toast), timeoutDuration);

  toast.addEventListener('mouseover', () => {
    clearTimeout(timerId);
  });

  toast.addEventListener('mouseout', () => {
    timerId = window.setTimeout(() => hideToast(toast), timeoutDuration);
  });
}

function hideToast(toast: HTMLElement) {
  toast.style.transform = 'translateX(100%)';
  toast.addEventListener(
    'transitionend',
    () => {
      toast.remove();
      updateToastPositions();
    },
    { once: true },
  );
}

export function addToastListener() {
  document.addEventListener('createToast', (event) => {
    const e = event as CustomEvent;

    const title = e.detail.title;
    const message = e.detail.message;
    const success = e.detail.success;

    createToast(title, message, 5000, success ? 'default' : 'destructive');
  });
}
